import React from 'react';
import { Button, Stack, TextField, Typography } from '@mui/material';
import UserSearchModal, { type User } from '../../Components/UserSearchModal';

type PersonalizationMemberPickerProps = {
  selectedUserID?: string | null;
  selectedDisplayName?: string | null;
  fullNameValue?: string | null;
  maxChars?: number;
  onChange: (next: {
    personalization_user_id: string | null;
    personalization_display_name: string | null;
    personalization_full_name: string | null;
  }) => void;
};

function fullNameFromUser(u: User): string {
  const combined = (
    u.full_name || `${u.first_name || ''} ${u.last_name || ''}`
  ).trim();
  return combined || u.id;
}

const PersonalizationMemberPicker: React.FC<
  PersonalizationMemberPickerProps
> = ({
  selectedUserID,
  selectedDisplayName,
  fullNameValue,
  maxChars = 255,
  onChange,
}) => {
  const [modalOpen, setModalOpen] = React.useState(false);

  const handleUserPicked = (user: User) => {
    const display = fullNameFromUser(user);
    onChange({
      personalization_user_id: user.id,
      personalization_display_name: display,
      personalization_full_name: display,
    });
    setModalOpen(false);
  };

  const label =
    selectedUserID && (selectedDisplayName || '').trim()
      ? selectedDisplayName!.trim()
      : 'Select member';

  return (
    <Stack spacing={1}>
      <Button
        variant="outlined"
        size="small"
        onClick={() => setModalOpen(true)}
        sx={{ alignSelf: 'flex-start' }}
      >
        {label}
      </Button>
      <UserSearchModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        userObjectRet={true}
        setUser={handleUserPicked}
        activeMembers
        statusFilter="active"
        allowSetToMe
      />
      <TextField
        size="small"
        fullWidth
        label="Name as it will appear"
        value={fullNameValue || ''}
        onChange={(e) => {
          const next = [...e.target.value].slice(0, maxChars).join('');
          onChange({
            personalization_user_id: selectedUserID || null,
            personalization_display_name: selectedDisplayName || null,
            personalization_full_name: next,
          });
        }}
        helperText={`${[...(fullNameValue || '')].length} / ${maxChars}`}
      />
      {!selectedUserID && (
        <Typography variant="caption" color="text.secondary">
          Member selection is required for this personalized item.
        </Typography>
      )}
    </Stack>
  );
};

export default PersonalizationMemberPicker;
